import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil } from "lucide-react";
import { api, errMsg } from "../../lib/api";
import { Field, SearchBox, matchQ } from "../common";
import { Modal } from "../shared";
import { useLang } from "../../lib/i18n";

function Input({ f, value, onChange }) {
  const { tr } = useLang();
  if (f.type === "select") {
    return (
      <select className="tl-select" value={value ?? ""} onChange={(e) => onChange(e.target.value || null)} data-testid={`field-${f.key}`}>
        {f.options.map(([v, l]) => <option key={v} value={v}>{tr(l)}</option>)}
      </select>
    );
  }
  if (f.type === "checkbox") {
    return <input type="checkbox" className="w-6 h-6" checked={!!value} onChange={(e) => onChange(e.target.checked)} data-testid={`field-${f.key}`} />;
  }
  return <input className="tl-input" type={f.type || "text"} value={value ?? ""} onChange={(e) => onChange(f.type === "number" ? (e.target.value === "" ? null : Number(e.target.value)) : e.target.value)} data-testid={`field-${f.key}`} />;
}

export default function SimpleCrud({ endpoint, fields, columns, defaults, canEdit, title, rowActions }) {
  const { tr } = useLang();
  const [rows, setRows] = useState([]);
  const [edit, setEdit] = useState(null);
  const [q, setQ] = useState("");
  const shown = rows.filter((r) => matchQ(q, ...columns.map(([k, , fmt]) => { const v = fmt ? fmt(r[k], r) : r[k]; return typeof v === "object" ? "" : String(v ?? ""); })));
  const load = useCallback(() => api.get(endpoint).then((r) => setRows(r.data)).catch(() => setRows([])), [endpoint]);
  useEffect(() => {
    load();
  }, [load]);
  const save = async () => {
    try {
      const body = Object.fromEntries(fields.map((f) => [f.key, edit[f.key] === "" ? null : edit[f.key]]));
      if (edit.id) await api.patch(`${endpoint}/${edit.id}`, { ...body, email: edit.email });
      else await api.post(endpoint, body);
      toast.success("Enregistré");
      setEdit(null);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div>
      <div className="flex flex-wrap gap-2 mb-3">
        {canEdit && <button className="tl-btn tl-btn-primary" onClick={() => setEdit({ ...defaults })} data-testid={`add-${title}`}><Plus size={18} /> {tr("Ajouter")}</button>}
        <SearchBox value={q} onChange={setQ} testid={`search-${title}`} />
      </div>
      <div className="tl-card overflow-x-auto">
        <table className="tl-table" data-testid={`table-${title}`}>
          <thead><tr>{columns.map(([k, l]) => <th key={k}>{tr(l)}</th>)}<th /></tr></thead>
          <tbody>
            {shown.map((r) => (
              <tr key={r.id}>
                {columns.map(([k, , fmt]) => <td key={k}>{fmt ? fmt(r[k], r) : String(r[k] ?? "")}</td>)}
                <td className="whitespace-nowrap">
                  {canEdit && <button className="tl-btn tl-btn-ghost tl-btn-sm" onClick={() => setEdit({ ...r, password: "" })} data-testid={`edit-${title}-${r.id}`}><Pencil size={14} /></button>}
                  {rowActions && rowActions(r, load)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {edit && (
        <Modal title={tr(edit.id ? "Modifier" : "Ajouter")} onClose={() => setEdit(null)} testid={`form-${title}`}>
          <div className="grid sm:grid-cols-2 gap-3">
            {fields.map((f) => (
              <Field key={f.key} label={f.label}><Input f={f} value={edit[f.key]} onChange={(v) => setEdit({ ...edit, [f.key]: v })} /></Field>
            ))}
          </div>
          <button className="tl-btn tl-btn-primary mt-4 w-full" onClick={save} data-testid={`save-${title}`}>{tr("Enregistrer")}</button>
        </Modal>
      )}
    </div>
  );
}
